import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// kinde
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

// Layout components
import Layout from '@/designs-dev/layout/Layout';
import MenuLayout from '@/designs-dev/layout/MenuLayout';

// Pages
import Welcome from '@/designs-dev/Welcome';
import LoftHome from '@/designs-dev/loft/LoftHome';
import Changelog from '@/designs-dev/Changelog';
import About from '@/designs-dev/about/About';
import { LoftUserProvider } from '@/designs-dev/loft/loftUser';
import KareHeroApp from '@/karehero-app/KareHeroApp';
import HandoverSheet from '@/designs-dev/HandoverSheet';

// Call-to-Claim keeps its provider here, above the routes, because its steps
// share flow state across pages.
import { ClaimFlowProvider } from '@/james-playground/call-to-claim/ClaimFlowContext';

// Prototype routes and their redirects, generated from the Loft manifest.
import { loftRedirects, loftRoutes } from '@/designs-dev/loft/routes';
import { newPathFor } from '@/designs-dev/loft/projects';

import { useBackendSession } from '@/designs-dev/backend/useBackendSession';

import { token } from './token';

// The URL the tab first loaded at, captured before any auth redirect or route
// fallback can rewrite it. Kinde returns to the app origin after login (the
// redirectUri is fixed and allow-listed), so a deep link is otherwise lost on
// the round-trip — we stash it here and restore it once authenticated.
const initialUrl = window.location.pathname + window.location.search;
const initialIsDeepLink = window.location.pathname !== '/';

function App() {
  // hooks
  const { isAuthenticated, isLoading, getToken, user } = useKindeAuth();
  const navigate = useNavigate();

  // Preserve a deep link across the Kinde login round-trip: stash it while
  // signed out, then navigate back to it once signed in.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      if (initialIsDeepLink) sessionStorage.setItem('returnTo', initialUrl);
      return;
    }
    const returnTo = sessionStorage.getItem('returnTo');
    if (returnTo) {
      sessionStorage.removeItem('returnTo');
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useBackendSession(!isLoading && !!isAuthenticated);

  if (isLoading) {
    return <div>Signing in...</div>;
  }

  token.setTokenGetter(async () => {
    if (!getToken) return undefined;
    return await getToken();
  });

  if (isLoading) {
    return null;
  }

  return (
    <div className='app-container'>
      <ClaimFlowProvider>
        <LoftUserProvider
          user={{
            email: user?.email,
            givenName: user?.givenName,
            familyName: user?.familyName,
            picture: user?.picture,
          }}
        >
          <Routes>
            <Route element={<Layout />}>
              {!isAuthenticated && <Route path='/' element={<Welcome />} />}
            </Route>

            {/* Main App — prototype routes live behind Kinde auth, all inside
            MenuLayout so the designs.dev menu stays on the left. */}
            {isAuthenticated && (
              <Route element={<MenuLayout />}>
                <Route path='/' element={<LoftHome />} />
                <Route path='/changelog' element={<Changelog />} />
                <Route path='/about' element={<About />} />
                <Route path='/about/:section' element={<About />} />
                {/* Prototype pages and handovers, generated from the Loft
                    manifest, plus a redirect for every path they used to sit at. */}
                {loftRoutes()}
                {loftRedirects()}

                {/* The master prototype: the member app mounted whole, so it
                    navigates internally and the stage never remounts mid-flow. */}
                <Route path='/master/*' element={<KareHeroApp />} />

                {/* Loft's own furniture, outside the area scheme. */}
                <Route path='/handover' element={<HandoverSheet />} />
              </Route>
            )}

            {/* bare aliases so you can deep-link, e.g. /actions. They point at
                the screen's current home rather than at its old member-app path,
                so following one is a single hop. */}
            {isAuthenticated &&
              [
                'welcome',
                'signup',
                'signin',
                'forgot',
                'home',
                'actions',
                'care',
                'chat',
              ].map((p) => (
                <Route
                  key={p}
                  path={`/${p}`}
                  element={
                    <Navigate
                      to={newPathFor(`/karehero-app/${p}`) ?? '/'}
                      replace
                    />
                  }
                />
              ))}

            {/* Fallback */}
            <Route path='*' element={<Navigate to='/' replace />} />
          </Routes>
        </LoftUserProvider>
      </ClaimFlowProvider>
    </div>
  );
}

export default App;
